import { useCallback, useEffect, useMemo, useState } from 'react';
import { MessageCircle, RotateCcw, UserPlus } from 'lucide-react';
import { buildWaMeUrl } from '../utils/whatsappSupport';
import CoachProductTabs from '../components/coach/CoachProductTabs';
import { clearDemoState, loadDemoState, saveDemoState } from './demoStore';
import { createInitialDemoState, TRAINING_NAV } from './demoFixtures';
import DemoTraining from './DemoTraining';
import DemoFood from './DemoFood';
import DemoLanding from './DemoLanding';
import './demo.css';

/** Mismo destino que «Crear cuenta» en el login de Food Plan. */
const FOOD_REGISTER_PATH = '/login?register=1&service=food';
/** Mismo WhatsApp que el soporte de activación de Food Plan. */
const FOOD_SUPPORT_PHONE = '573192635819';
const FOOD_WA_MSG = 'Hola, necesito soporte para activar mi Food Plan.';

function useDemoState() {
  const [state, setState] = useState(() => loadDemoState() || createInitialDemoState());

  useEffect(() => {
    saveDemoState({ ...state, touched_at: Date.now() });
  }, [state]);

  const reset = useCallback(() => {
    clearDemoState();
    setState(createInitialDemoState());
  }, []);

  return [state, setState, reset];
}

export default function DemoApp() {
  const [state, setState, reset] = useDemoState();
  const [exitOpen, setExitOpen] = useState(false);
  const screen = state.ui.screen || 'landing';
  const product = state.ui.product;
  const trainingView = state.ui.trainingView;

  const waUrl = useMemo(() => buildWaMeUrl(FOOD_SUPPORT_PHONE, FOOD_WA_MSG), []);

  const setScreen = (screenNext, extra = {}) => {
    setState((prev) => ({ ...prev, ui: { ...prev.ui, screen: screenNext, ...extra } }));
  };

  const startFood = (audience = 'final') => {
    const visitorName = String(state.ui.visitorName || '').trim();
    const trainer = audience === 'entrenador';
    setScreen('food', {
      product: 'food',
      foodPersona: trainer ? 'entrenador' : 'final',
      foodView: trainer ? 'panel' : 'dashboard',
      guideOpen: true,
      guideStep: 0,
      visitorName,
    });
  };

  const setTrainingView = (trainingViewNext) => {
    setState((prev) => ({ ...prev, ui: { ...prev.ui, trainingView: trainingViewNext } }));
  };

  const finishAndCta = () => {
    clearDemoState();
    setExitOpen(true);
  };

  const goRegister = () => {
    clearDemoState();
    // /demo arranca un árbol aparte (main.jsx). Hay que recargar para abrir el wizard Food.
    window.location.assign(FOOD_REGISTER_PATH);
  };

  const exitModal = exitOpen ? (
    <div className="form-modal-overlay" role="dialog" aria-modal="true">
      <div className="form-modal d28d-admin-modal ui-light-panel surface-readable max-w-md w-full mx-4 rounded-2xl p-6 border border-slate-200 shadow-xl">
        <h2 className="text-xl font-bold text-stone-900 mb-2">Tus datos de demo se borraron</h2>
        <p className="text-sm text-stone-600 mb-4">
          Era solo un simulador en este dispositivo. Para usar Food Plan de verdad,
          crea tu cuenta o escríbenos al WhatsApp de soporte Food.
        </p>
        <div className="flex flex-col gap-2">
          <button type="button" className="btn-primary inline-flex items-center justify-center gap-2" onClick={goRegister}>
            <UserPlus size={16} /> Registrarme
          </button>
          <a className="btn-secondary inline-flex items-center justify-center gap-2 no-underline" href={waUrl} target="_blank" rel="noreferrer">
            <MessageCircle size={16} /> WhatsApp soporte
          </a>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              setExitOpen(false);
              reset();
            }}
          >
            Seguir explorando el demo
          </button>
        </div>
      </div>
    </div>
  ) : null;

  const demoFooter = (
    <footer className="demo-eco-footer">
      <span className="demo-eco-footer__badge">DEMO · sin registro · sin BD</span>
      <button type="button" className="btn-secondary text-sm inline-flex items-center gap-2" onClick={reset}>
        <RotateCcw size={14} /> Reiniciar demo
      </button>
      <button type="button" className="btn-primary text-sm" onClick={finishAndCta}>
        Terminé · quiero acceso real
      </button>
    </footer>
  );

  if (screen === 'landing') {
    return (
      <DemoLanding
        name={state.ui.visitorName || ''}
        setName={(visitorName) => setState((prev) => ({ ...prev, ui: { ...prev.ui, visitorName } }))}
        onStart={startFood}
        onHiddenTrainer={() => setScreen('training', { product: 'training' })}
      />
    );
  }

  if (screen === 'food' || product === 'food') {
    return (
      <>
        <DemoFood
          state={state}
          setState={setState}
          onBackToEcosystem={() => setScreen('landing', { guideOpen: false })}
        />
        {demoFooter}
        {exitModal}
      </>
    );
  }

  return (
    <div className="dashboard-container">
      <nav className="navbar">
        <div className="navbar-brand">
          <span className="navbar-brand-title">
            <span className="brand-d28d">Ecosistema</span>{' '}
            <span className="brand-accent">Fit</span>
          </span>
          <span className="demo-nav-pill">DEMO</span>
        </div>
        <div className="navbar-menu">
          {TRAINING_NAV.map((item) => (
            <button
              key={item.id}
              type="button"
              className={trainingView === item.id ? 'nav-link active' : 'nav-link'}
              onClick={() => setTrainingView(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="navbar-user">
          <span className="user-name">{state.coach.nombre}</span>
          <button type="button" className="btn-logout" onClick={finishAndCta}>
            Salir del demo
          </button>
        </div>
      </nav>

      <div className="dashboard-content demo-eco-content">
        <CoachProductTabs
          active="training"
          onSelectTraining={() => setScreen('training', { product: 'training' })}
          onSelectFood={() => setScreen('food', { product: 'food', foodPersona: 'final', foodView: 'dashboard' })}
        />
        <DemoTraining state={state} setState={setState} />
      </div>

      {demoFooter}
      {exitModal}
    </div>
  );
}
